import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { CompanyPageData } from '../types/company-page';
import { __ } from '@/hub-shared/utils/i18n';
import ModuleCard from '../components/modules/ModuleCard';
import GeneralModule from '../components/modules/GeneralModule';
import ModuleSyncButton from '../components/modules/ModuleSyncButton';
import ProfileArtifactImportSection from '../components/profile/ProfileArtifactImportSection';
import CompanyDossierAccessPicker from '../../../companies/src/shared/CompanyDossierAccessPicker';
import CompanySignatoryPanel from '../components/signatory/CompanySignatoryPanel';
import useEditMode from '../hooks/useEditMode';

type InfoTabProps = {
  data: CompanyPageData;
  queryKey: unknown[];
  bootstrapKey: string;
  /** File store live: FEC/DSN files are deposited in the "Fichiers" tab instead. */
  fileStoreLive?: boolean;
};

const InfoTab = ({ data, queryKey, bootstrapKey, fileStoreLive = false }: InfoTabProps) => {
  const queryClient = useQueryClient();
  const { isEditing, localData, toggle, cancel, setField, save, isSaving, saveError } =
    useEditMode(data, `company/${data.id}`);

  const [manualAttributionUserIds, setManualAttributionUserIds] = useState<string[]>([]);

  useEffect(() => {
    setManualAttributionUserIds((data.manual_attribution_user_ids ?? []).map(String));
  }, [data]);

  const handleSave = () => {
    const manualIds = manualAttributionUserIds
      .map((id) => parseInt(id, 10))
      .filter((n) => Number.isFinite(n) && n > 0);

    void save(
      bootstrapKey,
      (updatedData) => {
        if (updatedData) {
          queryClient.setQueryData(queryKey, updatedData);
          setManualAttributionUserIds(
            (updatedData.manual_attribution_user_ids ?? []).map(String),
          );
        }
        void queryClient.invalidateQueries({ queryKey });
      },
      { manual_attribution_user_ids: manualIds },
    );
  };

  const handleCancel = () => {
    setManualAttributionUserIds((data.manual_attribution_user_ids ?? []).map(String));
    cancel();
  };

  return (
    <div className="info-tab">
      {!fileStoreLive && <ProfileArtifactImportSection companyId={data.id} bootstrapKey={bootstrapKey} />}

      <ModuleCard title={__('Informations générales')}>
        <GeneralModule data={localData} isEditing={isEditing} onChange={setField} />
      </ModuleCard>

      {isEditing ? (
        <CompanyDossierAccessPicker
          bootstrapKey={bootstrapKey}
          idPrefix={`company-page-${data.id}-attribution`}
          value={manualAttributionUserIds}
          onChange={setManualAttributionUserIds}
          disabled={isSaving}
        />
      ) : null}

      {data.modules.map((mod) => (
        <ModuleCard
          key={mod.provider_slug}
          title={mod.label}
          syncButton={
            <ModuleSyncButton
              companyId={data.id}
              providerSlug={mod.provider_slug}
              bootstrapKey={bootstrapKey}
            />
          }
        >
          {Object.entries(mod.data).length === 0 ? (
            <p className="module-card__empty">{__('Aucune donnée synchronisée.')}</p>
          ) : (
            <dl className="module-card__fields">
              {Object.entries(mod.data).map(([key, value]) => (
                <div key={key} className="module-card__field">
                  <dt className="module-card__field-label">{key}</dt>
                  <dd className="module-card__field-value">{String(value ?? '—')}</dd>
                </div>
              ))}
            </dl>
          )}
        </ModuleCard>
      ))}

      <ModuleCard title={__('Signature du rapport')}>
        <CompanySignatoryPanel companyId={data.id} bootstrapKey={bootstrapKey} />
      </ModuleCard>

      <div className="info-tab__actions">
        {isEditing ? (
          <>
            <button
              type="button"
              className="btn-primary"
              onClick={handleSave}
              disabled={isSaving}
            >
              {isSaving ? __('Enregistrement…') : __('Enregistrer')}
            </button>
            <button type="button" className="btn-secondary" onClick={handleCancel} disabled={isSaving}>
              {__('Annuler')}
            </button>
          </>
        ) : (
          <button type="button" className="btn-primary" onClick={toggle}>
            {__('Modifier')}
          </button>
        )}
        {saveError && <p className="info-tab__error">{saveError}</p>}
      </div>
    </div>
  );
};

export default InfoTab;
